import React from 'react';
import { useTranslation } from 'react-i18next';
import type { ResultElement } from '../../types/chat';
import { WeatherForecastCard } from './components/Weather';
import { ResultItemRendererFactory } from './renderers';
import { ResultItemSkeleton, WeatherSkeleton, StreamingIndicator } from '../SkeletonLoaders';
import type { StructuredResultItem, WeatherForecast } from './types';

interface StreamingResultsProps {
  data: {
    elements: ResultElement[];
    isComplete?: boolean;
    totalElements?: number;
  };
}

export const StreamingResultsRenderer: React.FC<StreamingResultsProps> = ({ data }) => {
  const { t } = useTranslation();
  const { elements = [], isComplete = false, totalElements = 0 } = data;
  
  // Extract typed elements
  const header = elements.find(e => e.type === 'header');
  const weatherElement = elements.find(e => e.type === 'weather');
  const items = elements.filter(e => e.type === 'item');
  const footer = elements.find(e => e.type === 'footer');
  
  // Convert weather element to WeatherForecast type
  const weather: WeatherForecast | undefined = weatherElement ? {
    summary_text: weatherElement.summary || '',
    details: {
      temperature_range: weatherElement.details?.temperature_range,
      conditions: weatherElement.details?.conditions,
      practical_advice: weatherElement.details?.practical_advice,
      location_name: weatherElement.details?.location_name,
      forecast_date: weatherElement.details?.forecast_date
    }
  } : undefined;

  // Group items by content type for display
  const groupItemsByType = (items: ResultElement[]) => {
    const groups: { [key: string]: StructuredResultItem[] } = {};
    
    items.forEach(item => {
      const type = item.content_type || 'unknown';
      if (!groups[type]) {
        groups[type] = [];
      }
      
      // Convert ResultElement to StructuredResultItem
      const structuredItem: StructuredResultItem = {
        title: item.title || '',
        description: item.description || '',
        location: item.location || '',
        url: item.url || '',
        content_type: type,
        distance_from_user: item.distance_from_user,
        distance_km: item.distance_km,
        difficulty_level: item.difficulty_level,
        elevation_gain: item.elevation_gain,
        event_date: item.event_date,
        event_time: item.event_time,
        venue: item.venue,
        organizer: item.organizer,
        attraction_type: item.attraction_type,
        opening_hours: item.opening_hours
      };
      
      groups[type].push(structuredItem);
    });
    
    return groups;
  };

  const getSectionTitle = (contentType: string, count: number) => {
    const titleKeys: Record<string, string> = {
      'bike_trail': 'results.sections.bikeTrails',
      'hiking_trail': 'results.sections.hikingTrails', 
      'attraction': 'results.sections.attractions',
      'event': 'results.sections.events'
    };
    
    const translatedTitle = titleKeys[contentType] 
      ? t(titleKeys[contentType]) 
      : contentType;
    
    return `${translatedTitle} (${count})`;
  };

  const groupedItems = groupItemsByType(items);

  return (
    <div className="space-y-3">
      {/* Header - appears immediately */}
      {header && (
        <div className="text-sm md:text-base text-gray-800 leading-relaxed fade-in">
          {header.text}
        </div>
      )}
      
      {/* Weather - with skeleton while loading */}
      {weather ? (
        <div className="fade-in">
          <WeatherForecastCard weather={weather} />
        </div>
      ) : !isComplete && (
        <WeatherSkeleton />
      )}
      
      {/* Result Items - progressive rendering with skeletons */}
      {Object.entries(groupedItems).map(([contentType, typeItems]) => (
        <div key={contentType}>
          <h4 className="text-md font-semibold text-gray-700 mb-2">
            {getSectionTitle(contentType, typeItems.length)}
          </h4>
          
          <div className="space-y-2">
            {typeItems.map((item, idx) => (
              <div key={idx} className="fade-in">
                <ResultItemRendererFactory item={item} />
              </div>
            ))}
          </div>
        </div>
      ))}
      
      {/* Streaming indicator and single skeleton for next item */}
      {!isComplete && (
        <div className="space-y-2">
          <StreamingIndicator />
          <ResultItemSkeleton />
        </div>
      )}
      
      {/* Footer - appears when present */}
      {footer && (
        <div className="text-sm md:text-base text-gray-800 font-semibold leading-relaxed mt-4 pt-3 border-t border-gray-200 fade-in">
          {footer.text}
        </div>
      )}
    </div>
  );
};

